import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import { HeroSection } from "../components/home/HeroSection";
import { TrustBar } from "../components/home/TrustBar";
import { WhyChooseUs } from "../components/home/WhyChooseUs";
import { ComparisonSection } from "../components/home/ComparisonSection";
import { TestimonialsSection } from "../components/home/TestimonialsSection";
import { FAQAccordion } from "../components/home/FAQAccordion";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Check,
  Home,
  Building2,
  Package,
  Piano,
  MapPin,
  Phone,
  ChevronRight
} from "lucide-react";
import { localBusinessSchema, createFAQSchema } from "../seo/schemas";

const FADE_IN_UP = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const STAGGER_DELAY = 0.1;
const STEP_STAGGER_DELAY = 0.15;

const HomePage = () => {
  const services = [
    {
      icon: Home,
      title: "House Removals",
      description: "From studio flats to 5-bed homes. We pack, load, drive, and unload — you just point us to the kettle.",
      href: "/services/house-removals",
      cta: "Plan Your House Move"
    },
    {
      icon: Building2,
      title: "Office Removals",
      description: "Weekend and out-of-hours moves to keep your business running. IT equipment handled with care.",
      href: "/services/office-removals",
      cta: "Plan Your Office Move"
    },
    {
      icon: Package,
      title: "Storage Solutions",
      description: "Secure, clean, accessible. Store for a week or a year — we'll collect and deliver when you're ready.",
      href: "/services/storage",
      cta: "Explore Storage Options"
    },
    {
      icon: Piano,
      title: "Specialist Moves",
      description: "Pianos, antiques, artwork, safes — our specialist team has the kit and know-how to move them safely.",
      href: "/services/specialist-moves",
      cta: "Learn About Specialist Moves"
    }
  ];

  const howItWorks = [
    {
      step: "01",
      title: "Tell Us About Your Move",
      description: "Use our 60-second calculator for an instant estimate, or book a free home survey for a fixed quote."
    },
    {
      step: "02",
      title: "Pick Your Date & Pay a Small Deposit",
      description: "Choose the day that works for you. A 25% deposit secures your slot — the rest on moving day."
    },
    {
      step: "03",
      title: "Relax — We've Got This",
      description: "Our team arrives on time, handles everything with care, and doesn't leave until you're happy."
    }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead
        title="Removals Cardiff | Trusted House & Office Movers | Hitech Transit"
        description="Professional removals in Cardiff & South Wales. House moves, office relocations, storage & specialist transport. Fully insured. Free instant quote in 60 seconds. Call 07346 209593."
        canonical="/"
        jsonLd={localBusinessSchema}
      />
      <Header />

      <HeroSection />
      <TrustBar />

      {/* Services */}
      <section className="section-padding bg-[#F8F9FA]">
        <div className="container-max">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-2 block">Our Services</span>
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold tracking-tight text-[#0F172A] mt-2">
              Whatever You're Moving, We've Done It Before
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link
                  to={service.href}
                  className="card-surface card-hover p-8 block h-full group"
                  data-testid={`service-card-${index}`}
                >
                  <div className="w-14 h-14 bg-[#FF5A1F]/10 flex items-center justify-center mb-6 group-hover:bg-[#FF5A1F] transition-colors">
                    <service.icon className="w-7 h-7 text-[#FF5A1F] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-xl font-['Outfit'] font-bold text-[#0F172A] mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {service.description}
                  </p>
                  <span className="text-[#FF5A1F] font-semibold text-sm inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                    {service.cta} <ChevronRight className="w-4 h-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <WhyChooseUs />
      <ComparisonSection />

      {/* How It Works */}
      <section className="section-padding">
        <div className="container-max">
          <div className="text-center mb-14">
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-2 block">Simple as 1-2-3</span>
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold tracking-tight text-[#0F172A] mt-2">
              How It Works
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {howItWorks.map((item, index) => (
              <motion.div
                key={item.step}
                className="text-center"
                {...FADE_IN_UP}
                transition={{ delay: index * STEP_STAGGER_DELAY }}
              >
                <div className="text-6xl font-['Outfit'] font-black text-[#FF5A1F]/15 mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-['Outfit'] font-bold text-[#0F172A] mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/instant-quote"
              className="btn-primary inline-flex items-center gap-2 text-base px-8 py-4"
              data-testid="how-it-works-cta"
            >
              Get Your Free Quote Now
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <TestimonialsSection />

      {/* Coverage Area */}
      <section className="section-padding">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-2 block">Where We Operate</span>
              <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold tracking-tight text-[#0F172A] mt-2 mb-4">
                Cardiff. London. Everywhere In Between.
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                Two bases, one standard. Whether you're moving down the road or across the country,
                you get the same insured, professional service from our own team.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {["Cardiff", "Newport", "Swansea", "London", "Bristol", "Birmingham", "Reading", "Oxford"].map((area) => (
                  <div key={area} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-[#FF5A1F]" />
                    <span className="font-medium text-sm">{area}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/areas-we-cover"
                className="btn-outline inline-flex items-center gap-2"
                data-testid="view-all-areas-btn"
              >
                See All Areas We Cover
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="relative">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/80ea756f-7ea3-4803-b754-18b59043e07a/images/acf49cfebb293b7cef04a42dfc41de23d515e47416b9f16c3c274dc10541f317.png"
                alt="Hitech Transit professional removals team loading furniture in Cardiff"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      <FAQAccordion />

      {/* Final CTA */}
      <section className="section-padding bg-[#FF5A1F]" data-testid="final-cta-section">
        <div className="container-max text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-['Outfit'] font-bold tracking-tight text-white mb-4">
            Ready to Take the Stress Out of Moving?
          </h2>
          <p className="text-white/90 text-lg max-w-2xl mx-auto mb-8">
            Join 500+ happy customers who chose Hitech Transit.
            Get your free, no-obligation quote right now — it takes less than a minute.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/instant-quote"
              className="bg-white text-[#FF5A1F] font-semibold px-8 py-4 inline-flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors text-base"
              data-testid="final-cta-quote-btn"
            >
              Get My Free Quote
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:07346209593"
              className="border-2 border-white text-white font-semibold px-8 py-4 inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
            >
              <Phone className="w-5 h-5" />
              07346 209593
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
